package er.prateek.crossly.ui.screens.home

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.backhandler.BackHandler
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.badges_count
import crossly.shared.generated.resources.badges_see_all
import crossly.shared.generated.resources.badges_title
import crossly.shared.generated.resources.coach_resume_home_body
import crossly.shared.generated.resources.coach_resume_home_title
import crossly.shared.generated.resources.coach_resume_kicker
import crossly.shared.generated.resources.home_choose_mode
import crossly.shared.generated.resources.home_daily
import crossly.shared.generated.resources.home_daily_done
import crossly.shared.generated.resources.home_daily_subtitle
import crossly.shared.generated.resources.home_progress_label
import crossly.shared.generated.resources.home_resume
import crossly.shared.generated.resources.home_resume_detail
import crossly.shared.generated.resources.home_stat_solved
import crossly.shared.generated.resources.home_stat_stars
import crossly.shared.generated.resources.home_stat_streak
import crossly.shared.generated.resources.home_tagline
import crossly.shared.generated.resources.home_title
import crossly.shared.generated.resources.mode_brainstorm_tag
import crossly.shared.generated.resources.mode_solved_count
import crossly.shared.generated.resources.share_streak_text
import er.prateek.crossly.ads.AdPlacement
import er.prateek.crossly.config.ModesConfig
import er.prateek.crossly.data.model.DifficultyTier
import er.prateek.crossly.data.model.SavedPuzzleSession
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.progression.BrainstormSession
import er.prateek.crossly.share.ShareOverlay
import er.prateek.crossly.share.formatSpokenDuration
import er.prateek.crossly.ui.achievements.AchievementMedal
import er.prateek.crossly.ui.achievements.NewTag
import er.prateek.crossly.ui.coach.CoachOverlay
import er.prateek.crossly.ui.coach.reportCoachBounds
import er.prateek.crossly.ui.components.CenteredRow
import er.prateek.crossly.ui.components.GameCard
import er.prateek.crossly.ui.components.GameIconButton
import er.prateek.crossly.ui.components.GameScreen
import er.prateek.crossly.ui.components.IconCheck
import er.prateek.crossly.ui.components.IconChevron
import er.prateek.crossly.ui.components.IconFlame
import er.prateek.crossly.ui.components.IconLogoMark
import er.prateek.crossly.ui.components.IconPlay
import er.prateek.crossly.ui.components.IconSliders
import er.prateek.crossly.ui.components.IconStar
import er.prateek.crossly.ui.components.IconTrophy
import er.prateek.crossly.ui.components.LabeledProgress
import er.prateek.crossly.ui.components.ModeBadge
import er.prateek.crossly.ui.components.PlayMode
import er.prateek.crossly.ui.components.StatChip
import er.prateek.crossly.ui.components.StreakBadge
import er.prateek.crossly.ui.components.tint
import er.prateek.crossly.ui.notifications.NotificationPermissionSheet
import er.prateek.crossly.ui.streak.StreakCard
import er.prateek.crossly.ui.streak.StreakRestoreSheet
import er.prateek.crossly.ui.theme.Radius
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import er.prateek.crossly.ui.theme.window
import org.jetbrains.compose.resources.stringResource

/** Collection totals shown on the home hub; recomputed whenever content loads. */
private class HomeStats(val earnedStars: Int, val maxStars: Int, val solved: Int, val total: Int)

@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun HomeScreen(
    onPlay: () -> Unit,
    onPlayDaily: () -> Unit,
    onPlayMath: () -> Unit,
    onPlayRiddles: () -> Unit,
    onPlayBrainstorm: () -> Unit,
    onOpenAchievements: () -> Unit,
    onOpenSettings: () -> Unit,
    onResume: (SavedPuzzleSession) -> Unit,
) {
    val window = MaterialTheme.window
    // Any trip back to Home ends a Brainstorm run.
    LaunchedEffect(Unit) { BrainstormSession.stop(reason = "left_run") }
    val modesConfig = AppContainer.appConfig().modes
    val streakManager = AppContainer.streakManager
    val playStreak = streakManager.displayStreak()
    val dailyDone = AppContainer.dailyPuzzleManager.hasCompletedToday()
    val savedSession = AppContainer.savedSessionManager.session
    var resumeCardBounds by remember { mutableStateOf(Rect.Zero) }
    var resumeCoachDismissed by remember { mutableStateOf(false) }
    val resumeCoachVisible = savedSession != null &&
        !resumeCoachDismissed &&
        !AppContainer.coachPrefsStore.hasSeenResumeCoach()

    LaunchedEffect(resumeCoachVisible, savedSession?.puzzleId) {
        if (!resumeCoachVisible) return@LaunchedEffect
        val session = savedSession ?: return@LaunchedEffect
        AppContainer.analytics.logEvent(
            AnalyticsEvents.RESUME_COACH_SHOWN,
            mapOf("puzzle_id" to session.puzzleId, "is_daily" to session.isDaily, "surface" to "home"),
        )
    }

    fun finishResumeCoach(skipped: Boolean) {
        AppContainer.coachPrefsStore.markResumeCoachSeen()
        AppContainer.analytics.logEvent(
            AnalyticsEvents.RESUME_COACH_FINISHED,
            mapOf("skipped" to skipped, "surface" to "home"),
        )
        resumeCoachDismissed = true
    }

    BackHandler(enabled = resumeCoachVisible) { finishResumeCoach(true) }

    var stats by remember { mutableStateOf<HomeStats?>(null) }
    LaunchedEffect(Unit) {
        val puzzles = DifficultyTier.entries.flatMap { AppContainer.contentRepository.puzzlesByTier(it) }
        val progression = AppContainer.progressionManager
        stats = HomeStats(
            earnedStars = puzzles.sumOf { progression.starsFor(it.id) },
            maxStars = puzzles.size * 3,
            solved = puzzles.count { progression.starsFor(it.id) > 0 },
            total = puzzles.size,
        )
    }

    // A frozen streak opens its restore sheet once per break; otherwise the reminders soft-ask
    // may show (at most per its own cooldown). Never both on one visit -- one ask at a time.
    var restoreSheetSource by remember { mutableStateOf<String?>(null) }
    // The share overlays (name prompt, challenge offers) arrive asynchronously after Remote Config
    // warms up, so they can land after this screen already decided to open a sheet. Home's own
    // modals step aside while one is up and come back once it closes -- never two at once.
    val shareUi by AppContainer.shareManager.ui.collectAsState()
    val shareOverlayIdle = shareUi.overlay == ShareOverlay.None
    var showNotificationSoftAsk by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        if (!resumeCoachVisible && streakManager.shouldAutoPromptRestore()) {
            streakManager.markRestorePrompted()
            restoreSheetSource = "home_auto"
        } else if (AppContainer.notificationCoordinator.shouldShowSoftAsk()) {
            showNotificationSoftAsk = true
            AppContainer.notificationCoordinator.onSoftAskShown()
        }
    }

    fun logHomeTap(event: String, extra: Map<String, Any> = emptyMap()) {
        AppContainer.analytics.logEvent(
            event,
            mapOf(
                "streak" to playStreak,
                "daily_done" to dailyDone,
                "has_saved_session" to (savedSession != null),
                "stars_earned" to (stats?.earnedStars ?: -1),
                "puzzles_solved" to (stats?.solved ?: -1),
            ) + extra,
        )
    }
    val onModeTapped: (PlayMode) -> Unit = { mode ->
        if (mode == PlayMode.ALPHABETS) logHomeTap(AnalyticsEvents.HOME_PLAY_TAPPED)
        logHomeTap(AnalyticsEvents.HOME_MODE_TAPPED, mapOf("mode" to mode.analyticsName))
        when (mode) {
            PlayMode.ALPHABETS -> onPlay()
            PlayMode.MATHEMATICS -> onPlayMath()
            PlayMode.RIDDLES -> onPlayRiddles()
            PlayMode.BRAINSTORM -> onPlayBrainstorm()
        }
    }
    val onPlayDailyTracked: () -> Unit = { logHomeTap(AnalyticsEvents.HOME_DAILY_TAPPED); onPlayDaily() }
    val onOpenSettingsTracked: () -> Unit = { logHomeTap(AnalyticsEvents.HOME_SETTINGS_TAPPED); onOpenSettings() }
    val onResumeTracked: (SavedPuzzleSession) -> Unit = { session ->
        AppContainer.analytics.logEvent(
            AnalyticsEvents.HOME_RESUME_TAPPED,
            mapOf(
                "puzzle_id" to session.puzzleId,
                "is_daily" to session.isDaily,
                "percent_complete" to session.percentComplete,
                "elapsed_ms" to session.elapsedMs,
                "from_challenge" to session.fromChallenge,
            ),
        )
        onResume(session)
    }
    val shareStreakText = stringResource(Res.string.share_streak_text, playStreak)
    val onShareStreak: () -> Unit = {
        AppContainer.analytics.logEvent(AnalyticsEvents.STREAK_SHARED, mapOf("current_streak" to playStreak))
        AppContainer.shareManager.shareWithAppLink(AppContainer.shareManager.playerName(), shareStreakText)
    }

    GameScreen(
        adPlacement = AdPlacement.HOME,
        overlay = {
            if (resumeCoachVisible) {
                CoachOverlay(
                    stepIndex = 0,
                    stepCount = 1,
                    title = stringResource(Res.string.coach_resume_home_title),
                    body = stringResource(Res.string.coach_resume_home_body),
                    hole = resumeCardBounds,
                    kicker = stringResource(Res.string.coach_resume_kicker),
                    holeCornerRadius = 24.dp,
                    onNext = { finishResumeCoach(false) },
                    onSkip = { finishResumeCoach(true) },
                )
            }
            if (showNotificationSoftAsk && shareOverlayIdle) {
                NotificationPermissionSheet(
                    onTurnOnReminders = {
                        AppContainer.notificationCoordinator.onSoftAskSettingsTapped()
                        showNotificationSoftAsk = false
                    },
                    onDismiss = {
                        AppContainer.notificationCoordinator.onSoftAskDismissed()
                        showNotificationSoftAsk = false
                    },
                )
            }
            restoreSheetSource?.takeIf { shareOverlayIdle }?.let { source ->
                StreakRestoreSheet(source = source, onClose = { restoreSheetSource = null })
            }
        },
    ) {
        Column(modifier = Modifier.fillMaxSize()) {
            HomeHud(
                streak = playStreak,
                stars = AppContainer.progressionManager.starsAvailable(),
                hasNewBadges = AppContainer.achievementManager.hasUnseen(),
                onOpenAchievements = onOpenAchievements,
                onOpenSettings = onOpenSettingsTracked,
            )

            val resume: @Composable () -> Unit = {
                savedSession?.let {
                    ResumeCard(session = it, onClick = { onResumeTracked(it) }, modifier = Modifier.reportCoachBounds { resumeCardBounds = it })
                }
            }
            val streakCard: @Composable () -> Unit = {
                StreakCard(onShare = onShareStreak, onRestore = { restoreSheetSource = "home_card" })
            }
            val daily: @Composable () -> Unit = {
                DailyCard(streak = playStreak, done = dailyDone, onClick = onPlayDailyTracked)
            }

            if (window.isSideBySide) {
                // Wide windows split the hub: identity, streak and collection on the left, the ways
                // to play on the right. Each side scrolls on its own.
                Row(
                    modifier = Modifier.fillMaxSize(),
                    horizontalArrangement = Arrangement.spacedBy(Spacing.xxl),
                ) {
                    Column(
                        modifier = Modifier.weight(1f).verticalScroll(rememberScrollState()),
                        verticalArrangement = Arrangement.spacedBy(Spacing.lg),
                    ) {
                        Brand(alignment = Alignment.Start)
                        streakCard()
                        BadgeShelfCard(onOpen = onOpenAchievements)
                        StatsCard(stats = stats, longestStreak = streakManager.streak.longest)
                    }
                    Column(
                        // Capped: a full-bleed card on a wide display reads as a banner.
                        modifier = Modifier.weight(1f).widthIn(max = 480.dp).verticalScroll(rememberScrollState()),
                        verticalArrangement = Arrangement.spacedBy(Spacing.md),
                    ) {
                        resume()
                        ModeHub(modesConfig, onModeTapped)
                        daily()
                    }
                }
            } else {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState())
                        .padding(bottom = Spacing.lg),
                    verticalArrangement = Arrangement.spacedBy(Spacing.md),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Brand(alignment = Alignment.CenterHorizontally)
                    resume()
                    ModeHub(modesConfig, onModeTapped)
                    daily()
                    streakCard()
                    BadgeShelfCard(onOpen = onOpenAchievements)
                    StatsCard(stats = stats, longestStreak = streakManager.streak.longest)
                }
            }
        }
    }
}

/** Top bar: live streak and spendable stars on the left, trophy cabinet and settings on the right. */
@Composable
private fun HomeHud(
    streak: Int,
    stars: Int,
    hasNewBadges: Boolean,
    onOpenAchievements: () -> Unit,
    onOpenSettings: () -> Unit,
) {
    val colors = MaterialTheme.game
    CenteredRow(modifier = Modifier.fillMaxWidth(), spacing = Spacing.sm) {
        StreakBadge(streak = streak)
        StatChip(
            value = stars.toString(),
            tint = colors.gold,
            icon = { tint -> IconStar(size = 16.dp, tint = tint) },
        )
        Spacer(Modifier.weight(1f))
        Box {
            GameIconButton(onClick = onOpenAchievements) { tint -> IconTrophy(size = 20.dp, tint = tint) }
            if (hasNewBadges) {
                Box(
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .offset(x = 2.dp, y = (-2).dp)
                        .size(12.dp)
                        .background(colors.flame, CircleShape),
                )
            }
        }
        GameIconButton(onClick = onOpenSettings) { tint -> IconSliders(size = 20.dp, tint = tint) }
    }
}

@Composable
private fun Brand(alignment: Alignment.Horizontal) {
    val colors = MaterialTheme.game
    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = alignment,
        verticalArrangement = Arrangement.spacedBy(Spacing.xs),
    ) {
        IconLogoMark(
            size = MaterialTheme.window.pick(compact = 44.dp, medium = 64.dp),
            tint = colors.cellFill,
            accentTint = colors.accent,
            blockTint = colors.cellBlock,
        )
        Text(
            text = stringResource(Res.string.home_title).uppercase(),
            // Gradient-filled wordmark: the app's signature, keyed to the palette.
            // Sized per breakpoint and pinned to one line — a wrapped logo reads
            // as a layout bug, not as branding.
            style = MaterialTheme.window
                .pick(
                    compact = MaterialTheme.typography.displaySmall,
                    medium = MaterialTheme.typography.displayLarge,
                )
                .merge(TextStyle(brush = colors.accentBrush)),
            maxLines = 1,
        )
        Text(
            text = stringResource(Res.string.home_tagline),
            style = MaterialTheme.typography.bodyMedium,
            color = colors.inkMuted,
            textAlign = if (alignment == Alignment.CenterHorizontally) TextAlign.Center else TextAlign.Start,
        )
    }
}

/**
 * The ways to play: Alphabets + Mathematics as a pair of equal badge tiles, Riddles beneath, and
 * the mixed Brainstorm run as the hero card at the bottom.
 */
@Composable
private fun ModeHub(modesConfig: ModesConfig, onModeTapped: (PlayMode) -> Unit) {
    val colors = MaterialTheme.game
    Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(Spacing.md)) {
        Text(
            text = stringResource(Res.string.home_choose_mode).uppercase(),
            style = MaterialTheme.typography.labelLarge.copy(letterSpacing = 1.5.sp),
            color = colors.inkMuted,
            modifier = Modifier.padding(top = Spacing.xs),
        )
        // IntrinsicSize.Min + fillMaxHeight keeps the pair the same height whatever the copy wraps to.
        Row(
            modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min),
            horizontalArrangement = Arrangement.spacedBy(Spacing.md),
        ) {
            // GameCard puts its modifier on the inner face, so RowScope.weight has to go on a
            // wrapper -- applied to the card directly it's silently ignored.
            Box(modifier = Modifier.weight(1f).fillMaxHeight()) {
                ModeTile(
                    mode = PlayMode.ALPHABETS,
                    solved = AppContainer.progressionManager.completedPuzzleCount(),
                    onClick = { onModeTapped(PlayMode.ALPHABETS) },
                )
            }
            if (modesConfig.mathCrossword.enabled) {
                Box(modifier = Modifier.weight(1f).fillMaxHeight()) {
                    ModeTile(
                        mode = PlayMode.MATHEMATICS,
                        solved = AppContainer.mathProgressionManager.completedCount(),
                        onClick = { onModeTapped(PlayMode.MATHEMATICS) },
                    )
                }
            }
        }
        if (modesConfig.riddles.enabled) {
            ModeStrip(
                mode = PlayMode.RIDDLES,
                solved = AppContainer.riddleProgressionManager.completedCount(),
                onClick = { onModeTapped(PlayMode.RIDDLES) },
            )
        }
        if (modesConfig.brainstorm.enabled && modesConfig.mathCrossword.enabled && modesConfig.riddles.enabled) {
            BrainstormCard(onClick = { onModeTapped(PlayMode.BRAINSTORM) })
        }
    }
}

@Composable
private fun ModeTile(mode: PlayMode, solved: Int, onClick: () -> Unit) {
    val colors = MaterialTheme.game
    val tint = mode.tint()
    GameCard(
        modifier = Modifier.fillMaxSize(),
        onClick = onClick,
        accentGlow = tint.copy(alpha = 0.5f),
        contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = Spacing.md, vertical = Spacing.lg),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(Spacing.xs),
        ) {
            ModeBadge(mode, size = 64.dp)
            Text(
                text = stringResource(mode.title),
                style = MaterialTheme.typography.titleLarge,
                color = colors.ink,
                maxLines = 1,
                modifier = Modifier.padding(top = Spacing.xs),
            )
            Text(
                text = stringResource(mode.subtitle),
                style = MaterialTheme.typography.bodySmall,
                color = colors.inkMuted,
                textAlign = TextAlign.Center,
                maxLines = 2,
            )
            SolvedPill(solved = solved, tint = tint, modifier = Modifier.padding(top = Spacing.xs))
        }
    }
}

@Composable
private fun ModeStrip(mode: PlayMode, solved: Int, onClick: () -> Unit) {
    val colors = MaterialTheme.game
    val tint = mode.tint()
    GameCard(modifier = Modifier.fillMaxWidth(), onClick = onClick, accentGlow = tint.copy(alpha = 0.5f)) {
        CenteredRow(modifier = Modifier.fillMaxWidth(), spacing = Spacing.md) {
            ModeBadge(mode, size = 52.dp)
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(Spacing.xxs)) {
                Text(text = stringResource(mode.title), style = MaterialTheme.typography.titleLarge, color = colors.ink)
                Text(text = stringResource(mode.subtitle), style = MaterialTheme.typography.bodySmall, color = colors.inkMuted)
                SolvedPill(solved = solved, tint = tint, modifier = Modifier.padding(top = Spacing.xxs))
            }
            IconChevron(size = 18.dp, tint = colors.inkMuted, pointsLeft = false)
        }
    }
}

/** The hero card: three overlapping mode badges say "a bit of everything" before any copy does. */
@Composable
private fun BrainstormCard(onClick: () -> Unit) {
    val colors = MaterialTheme.game
    val tint = PlayMode.BRAINSTORM.tint()
    GameCard(modifier = Modifier.fillMaxWidth(), onClick = onClick, accentGlow = tint.copy(alpha = 0.8f)) {
        CenteredRow(modifier = Modifier.fillMaxWidth(), spacing = Spacing.md) {
            Box(modifier = Modifier.size(width = 76.dp, height = 52.dp)) {
                ModeBadge(PlayMode.ALPHABETS, size = 36.dp, modifier = Modifier.align(Alignment.TopStart))
                ModeBadge(PlayMode.MATHEMATICS, size = 36.dp, modifier = Modifier.align(Alignment.TopEnd))
                ModeBadge(PlayMode.RIDDLES, size = 36.dp, modifier = Modifier.align(Alignment.BottomCenter))
            }
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(Spacing.xxs)) {
                CenteredRow(spacing = Spacing.sm) {
                    Text(text = stringResource(PlayMode.BRAINSTORM.title), style = MaterialTheme.typography.titleLarge, color = colors.ink)
                    Text(
                        text = stringResource(Res.string.mode_brainstorm_tag),
                        style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Black, letterSpacing = 1.sp),
                        color = colors.onAccent,
                        modifier = Modifier
                            .background(colors.goldBrush, RoundedCornerShape(Radius.chip))
                            .padding(horizontal = Spacing.sm, vertical = Spacing.xxs),
                    )
                }
                Text(text = stringResource(PlayMode.BRAINSTORM.subtitle), style = MaterialTheme.typography.bodySmall, color = colors.inkMuted)
            }
            IconPlay(size = 22.dp, tint = tint)
        }
    }
}

@Composable
private fun SolvedPill(solved: Int, tint: Color, modifier: Modifier = Modifier) {
    val colors = MaterialTheme.game
    CenteredRow(
        modifier = modifier
            .background(tint.copy(alpha = 0.14f), RoundedCornerShape(Radius.chip))
            .padding(horizontal = Spacing.sm, vertical = Spacing.xxs),
        spacing = Spacing.xs,
    ) {
        IconCheck(size = 12.dp, tint = tint)
        Text(
            text = stringResource(Res.string.mode_solved_count, solved),
            style = MaterialTheme.typography.labelSmall,
            color = colors.ink,
        )
    }
}

/** A peek at the trophy cabinet: progress plus the five most recent (or next) medals. */
@Composable
private fun BadgeShelfCard(onOpen: () -> Unit) {
    val colors = MaterialTheme.game
    val manager = AppContainer.achievementManager
    val total = er.prateek.crossly.progression.achievements.Achievement.entries.size
    GameCard(modifier = Modifier.fillMaxWidth(), onClick = onOpen) {
        CenteredRow(modifier = Modifier.fillMaxWidth(), spacing = Spacing.sm) {
            IconTrophy(size = 22.dp, tint = colors.gold)
            Text(
                text = stringResource(Res.string.badges_title),
                style = MaterialTheme.typography.titleLarge,
                color = colors.ink,
            )
            if (manager.hasUnseen()) NewTag()
            Spacer(Modifier.weight(1f))
            Text(
                text = stringResource(Res.string.badges_count, manager.unlockedCount(), total),
                style = MaterialTheme.typography.labelLarge,
                color = colors.gold,
            )
        }
        Row(
            modifier = Modifier.fillMaxWidth().padding(top = Spacing.md),
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            manager.shelf().take(5).forEach { achievement ->
                AchievementMedal(achievement = achievement, unlocked = manager.isUnlocked(achievement), size = 48.dp)
            }
        }
        CenteredRow(modifier = Modifier.fillMaxWidth().padding(top = Spacing.md), spacing = Spacing.xs) {
            Spacer(Modifier.weight(1f))
            Text(
                text = stringResource(Res.string.badges_see_all),
                style = MaterialTheme.typography.labelLarge,
                color = colors.accent,
            )
            IconChevron(size = 14.dp, tint = colors.accent, pointsLeft = false)
        }
    }
}

@Composable
private fun ResumeCard(
    session: SavedPuzzleSession,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val colors = MaterialTheme.game
    GameCard(
        modifier = modifier.fillMaxWidth(),
        onClick = onClick,
        accentGlow = colors.gold.copy(alpha = 0.7f),
    ) {
        CenteredRow(modifier = Modifier.fillMaxWidth(), spacing = Spacing.md) {
            Box(
                modifier = Modifier.size(44.dp),
                contentAlignment = Alignment.Center,
            ) {
                IconPlay(size = 28.dp, tint = colors.gold)
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = session.puzzleLabel,
                    style = MaterialTheme.typography.titleLarge,
                    color = colors.ink,
                )
                Text(
                    text = stringResource(Res.string.home_resume),
                    style = MaterialTheme.typography.titleMedium,
                    color = colors.gold,
                )
                Text(
                    text = stringResource(
                        Res.string.home_resume_detail,
                        "${session.percentComplete}%",
                        formatSpokenDuration(session.elapsedMs),
                    ),
                    style = MaterialTheme.typography.bodySmall,
                    color = colors.inkMuted,
                    modifier = Modifier.padding(top = Spacing.sm),
                )
            }
            IconChevron(size = 18.dp, tint = colors.inkMuted, pointsLeft = false)
        }
    }
}

@Composable
private fun DailyCard(streak: Int, done: Boolean, onClick: () -> Unit) {
    val colors = MaterialTheme.game
    GameCard(
        modifier = Modifier.fillMaxWidth(),
        onClick = onClick,
        // A live streak makes the card glow — the strongest pull back tomorrow.
        accentGlow = if (streak > 0) colors.flame.copy(alpha = 0.65f) else null,
    ) {
        CenteredRow(modifier = Modifier.fillMaxWidth(), spacing = Spacing.md) {
            Box(
                modifier = Modifier.size(44.dp),
                contentAlignment = Alignment.Center,
            ) {
                IconFlame(
                    size = 40.dp,
                    tint = if (streak > 0) colors.flame else colors.locked,
                    coreTint = if (streak > 0) colors.gold else null,
                )
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.home_daily),
                    style = MaterialTheme.typography.titleLarge,
                    color = colors.ink,
                )
                CenteredRow(spacing = Spacing.xs) {
                    if (done) {
                        IconCheck(size = 14.dp, tint = colors.success)
                        Text(
                            text = stringResource(Res.string.home_daily_done),
                            style = MaterialTheme.typography.bodySmall,
                            color = colors.success,
                        )
                    } else {
                        Text(
                            text = stringResource(Res.string.home_daily_subtitle),
                            style = MaterialTheme.typography.bodySmall,
                            color = colors.inkMuted,
                        )
                    }
                }
            }
            IconChevron(size = 18.dp, tint = colors.inkMuted, pointsLeft = false)
        }
    }
}

@Composable
private fun StatsCard(stats: HomeStats?, longestStreak: Int) {
    val colors = MaterialTheme.game
    GameCard(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceEvenly,
        ) {
            StatColumn(
                value = stats?.earnedStars?.toString() ?: "—",
                label = stringResource(Res.string.home_stat_stars),
                tint = colors.gold,
            )
            StatColumn(
                value = stats?.let { "${it.solved}" } ?: "—",
                label = stringResource(Res.string.home_stat_solved),
                tint = colors.accent,
            )
            StatColumn(
                value = longestStreak.toString(),
                label = stringResource(Res.string.home_stat_streak),
                tint = colors.flame,
            )
        }
        if (stats != null && stats.maxStars > 0) {
            Box(modifier = Modifier.padding(top = Spacing.lg)) {
                LabeledProgress(
                    label = stringResource(Res.string.home_progress_label),
                    value = "${stats.earnedStars} / ${stats.maxStars}",
                    progress = stats.earnedStars.toFloat() / stats.maxStars,
                    tint = colors.gold,
                )
            }
        }
    }
}

@Composable
private fun StatColumn(value: String, label: String, tint: Color) {
    val colors = MaterialTheme.game
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Text(text = value, style = MaterialTheme.typography.headlineMedium, color = tint)
        Text(text = label, style = MaterialTheme.typography.labelSmall, color = colors.inkMuted)
    }
}
